import { typedApi } from "@/api/typed-client";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

// Re-export the shape of a freshly created key (includes raw_key, shown once)
export interface ApiKeyCreated {
  id: string;
  name: string;
  key_prefix: string;
  raw_key: string;
  expires_at: string | null;
  created_at: string;
}

export interface CreateApiKeyBody {
  name: string;
  rate_limit_rpm: number;
  expires_at?: string | null;
}

/**
 * Thrown by useCreateApiKey when the API returns 409 DuplicateError.
 * The create dialog catches this and shows an inline field error instead of a toast.
 */
export class DuplicateNameError extends Error {
  constructor(message = "A key with this name already exists.") {
    super(message);
    this.name = "DuplicateNameError";
  }
}

export function useCreateApiKey(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateApiKeyBody): Promise<ApiKeyCreated> => {
      const { data, error, response } = await typedApi.POST("/v1/projects/{project_id}/api-keys", {
        params: { path: { project_id: projectId } },
        body: {
          name: body.name,
          rate_limit_rpm: body.rate_limit_rpm,
          expires_at: body.expires_at,
        },
      });

      if (error) {
        // openapi-fetch: `error` IS the parsed JSON body of the error response.
        // The API middleware shapes it as { error: { code, message, details } }.
        // We must check `response.status` to know it's a 409, then inspect the code.
        if (
          response?.status === 409 &&
          (error as { error?: { code?: string } }).error?.code === "DuplicateError"
        ) {
          throw new DuplicateNameError();
        }
        const msg =
          (error as { error?: { message?: string } }).error?.message ?? "Create key failed";
        throw new Error(msg);
      }

      // Justified: ApiKeyCreated schema is compatible but openapi-fetch widens return type
      return data as unknown as ApiKeyCreated;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["api-keys", projectId] }),
    // No automatic toast.error — the create dialog handles errors inline.
  });
}

export function useRevokeApiKey(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (keyId: string) => {
      const { data, error } = await typedApi.POST(
        "/v1/projects/{project_id}/api-keys/{key_id}/revoke",
        {
          params: { path: { project_id: projectId, key_id: keyId } },
        },
      );
      if (error) {
        const msg = (error as { error?: { message?: string } }).error?.message ?? "Revoke failed";
        throw new Error(msg);
      }
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["api-keys", projectId] });
      toast.success("API key revoked");
    },
    onError: (e) => toast.error(`Revoke failed: ${(e as Error).message}`),
  });
}
